/**
 * Utility function to make API calls
 * Uses POST method for everything, with parameters in the body
 */


import { isBiggerVersion } from './utils.js';

// Keep track of the last AI request time for rate limiting
let lastAiRequestTime = 0;
const AI_REQUEST_DELAY_MS = 3000; // 2 seconds delay between AI requests


async function apiCall(apiUrl, body = {}, method = 'POST') {
  const token = localStorage.getItem('token');
  const language = localStorage.getItem('i18nextLng') || 'en';
  
  // Set up request options
  const options = {
    method: method,
    headers: {
      'Content-Type': 'application/json',
      'X-Language': language,
    },
  };

  // Add auth token if available
  if (token) {
    options.headers.Authorization = `Bearer ${token}`;
  }

  // Add request body
  if (Object.keys(body).length > 0) {
    options.body = JSON.stringify(body);
  }
  
  // Add delay for AI requests to avoid rate limiting
  if (apiUrl.includes('/api/rawAi')) {
    const now = Date.now();
    const timeSinceLastRequest = now - lastAiRequestTime;
    
    // If it's been less than the delay time since the last request, wait
    if (timeSinceLastRequest < AI_REQUEST_DELAY_MS) {
      const waitTime = AI_REQUEST_DELAY_MS - timeSinceLastRequest;
      console.log(`Rate limiting: Waiting ${waitTime}ms before next AI request`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    
    // Update the last request time
    lastAiRequestTime = Date.now();
  }
  
  // Make the request
  const response = await fetch(apiUrl, options);
  
  // Save server version to localStorage if present
  // Only update if server version is bigger than stored version, or if nothing is stored
  const serverVersion = response.headers.get('X-App-Version');
  if (serverVersion) {
    const storedVersion = localStorage.getItem('serverVersion');
    
    // Only update if:
    // 1. No version stored yet (first time), OR
    // 2. Server version is bigger than stored version
    if (!storedVersion || isBiggerVersion(serverVersion, storedVersion)) {
      localStorage.setItem('serverVersion', serverVersion);
    }
  }
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API call failed with status: ${response.status}`);
  }

  return await response.json();
}

export default apiCall; 